import React, { useState, useEffect, useRef } from 'react';
import { Trash2, Search, X } from 'lucide-react';
import CustomSpecsEditor from '../../../../shared/CustomSpecsEditor';
import API_BASE from '../../../../../utils/api.js';

const proxyImg = (url) =>
  url ? `${API_BASE}/api/upload/proxy-image?url=${encodeURIComponent(url)}` : '';

export default function AlternativeItemCard({ alt, index, catalog = [], onUpdate, onRemove, readOnly = false, isClientSelected = false }) {
  const trade = Number(alt.tradePrice) || 0;

  const fmt2 = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n.toFixed(2) : '';
  };
  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const [sellInput, setSellInput] = useState('');
  const [markupInput, setMarkupInput] = useState('');

  // Catalog search state
  const [catalogQuery, setCatalogQuery] = useState('');
  const [showCatalogResults, setShowCatalogResults] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    if (alt.lastEdited !== 'sell') {
      const computed =
        alt.markup?.type === '%'
          ? trade + trade * (Number(alt.markup?.value || 0) / 100)
          : trade + Number(alt.markup?.value || 0);
      setSellInput(fmt2(computed));
    }
    if (alt.lastEdited !== 'markup') {
      setMarkupInput(fmt2(alt.markup?.value || 0));
    }
  }, [trade, alt.markup, alt.lastEdited]);

  // Close catalog dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowCatalogResults(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filteredCatalog = catalog.filter((item) => {
    if (!catalogQuery.trim()) return true;
    const q = catalogQuery.toLowerCase();
    return [item.name, item.description, item.vendorName, item.category]
      .map((v) => String(v || '').toLowerCase())
      .join(' ')
      .includes(q);
  }).slice(0, 20);

  function pickFromCatalog(item) {
    // Pre-fill the alternative from the catalog item
    const newMarkup = item.markup || { type: '%', value: 0 };
    const newTrade = Number(item.tradePrice || 0);
    const newSell =
      newMarkup.type === '%'
        ? newTrade + newTrade * (Number(newMarkup.value || 0) / 100)
        : newTrade + Number(newMarkup.value || 0);
    const newQty = alt.qty || 1;
    const unitShipping = Number(item.shipping || 0);
    const unitTariff = Number(item.tariff || 0);

    onUpdate({
      name: item.name || '',
      description: item.description || '',
      imageUrl: item.imageUrl || '',
      tradePrice: newTrade,
      qty: newQty,
      markup: newMarkup,
      clientUnit: newSell,
      unitShipping,
      unitTariff,
      shipping: unitShipping * newQty,
      shippingMarkup: item.shippingMarkup || { type: '%', value: 0 },
      clientShipping: 0,
      tariff: unitTariff * newQty,
      tariffMarkup: item.tariffMarkup || { type: '%', value: 0 },
      clientTariff: 0,
      vendor: item.vendorName || item.vendor || '',
      unitType: item.unitType || 'Each',
      productItemId: item.itemId || null,
      color: item.color || '',
      finishes: item.finishes || '',
      materials: item.materials || '',
      dimensions: item.dimensions || {},
      customSpecs: Array.isArray(item.customSpecs) ? item.customSpecs : [],
      link: item.link || '',
      lastEdited: 'markup',
    });

    setCatalogQuery('');
    setShowCatalogResults(false);
  }

  const updateDimension = (key, value) =>
    onUpdate({ dimensions: { ...(alt.dimensions || {}), [key]: value } });

  const handleSellBlur = () => {
    const clean = toNum(sellInput);
    if (clean == null) return;
    const safeSell = Math.max(clean, 0);
    const newMarkup =
      alt.markup?.type === '%'
        ? trade > 0 ? ((safeSell - trade) / trade) * 100 : 0
        : safeSell - trade;
    setSellInput(fmt2(safeSell));
    setMarkupInput(fmt2(newMarkup));
    onUpdate({ clientUnit: safeSell, markup: { type: alt.markup?.type || '%', value: newMarkup }, lastEdited: 'sell' });
  };

  const handleMarkupBlur = () => {
    const m = toNum(markupInput);
    if (m == null) return;
    const safeMarkup = alt.markup?.type === '%' ? Math.max(m, -100) : m;
    const newSell = Math.max(
      alt.markup?.type === '%' ? trade + trade * (safeMarkup / 100) : trade + safeMarkup,
      0
    );
    setMarkupInput(fmt2(safeMarkup));
    setSellInput(fmt2(newSell));
    onUpdate({ markup: { type: alt.markup?.type || '%', value: safeMarkup }, clientUnit: newSell, lastEdited: 'markup' });
  };

  return (
    <div className={`alt-item-card${isClientSelected ? ' alt-item-card--client-selected' : ''}`}>
      <div className="alt-item-header">
        <span className="alt-item-label">Alt {index + 1}</span>
        {isClientSelected && (
          <span className="alt-client-selected-badge">✓ Client's Choice</span>
        )}
        {!readOnly && (
          <button className="alt-remove-btn" onClick={onRemove} title="Remove alternative">
            <Trash2 size={13} />
          </button>
        )}
      </div>

      {/* CATALOG SEARCH */}
      {!readOnly && catalog.length > 0 && (
        <div className="alt-catalog-search" ref={searchRef}>
          <div className="alt-catalog-input-wrap">
            <Search size={13} className="alt-catalog-icon" />
            <input
              className="if-input alt-catalog-input"
              placeholder="Search catalog to pre-fill…"
              value={catalogQuery}
              onChange={(e) => {
                setCatalogQuery(e.target.value);
                setShowCatalogResults(true);
              }}
              onFocus={() => setShowCatalogResults(true)}
            />
            {catalogQuery && (
              <button
                className="alt-catalog-clear"
                onClick={() => { setCatalogQuery(''); setShowCatalogResults(false); }}
              >
                <X size={12} />
              </button>
            )}
          </div>

          {showCatalogResults && (
            <div className="alt-catalog-results">
              {filteredCatalog.length === 0 ? (
                <div className="alt-catalog-empty">No matching items</div>
              ) : (
                filteredCatalog.map((item) => (
                  <button
                    key={item.itemId}
                    className="alt-catalog-result-row"
                    onMouseDown={() => pickFromCatalog(item)}
                  >
                    {item.imageUrl && (
                      <img src={proxyImg(item.imageUrl)} alt="" className="alt-catalog-thumb" />
                    )}
                    <div className="alt-catalog-info">
                      <span className="alt-catalog-name">{item.name}</span>
                      {item.vendorName && (
                        <span className="alt-catalog-vendor">{item.vendorName}</span>
                      )}
                    </div>
                    <span className="alt-catalog-price">
                      ${Number(item.tradePrice || 0).toFixed(2)}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}

      <div className="alt-item-fields">
        {/* Name */}
        <div className="alt-field-group">
          <label>Name</label>
          <input
            className="if-input"
            placeholder="Alternative item name"
            value={alt.name || ''}
            readOnly={readOnly}
            onChange={readOnly ? undefined : (e) => onUpdate({ name: e.target.value })}
          />
        </div>

        {/* Description */}
        <div className="alt-field-group alt-field-wide">
          <label>Description</label>
          <textarea
            className="if-textarea"
            placeholder="Description"
            rows={2}
            value={alt.description || ''}
            readOnly={readOnly}
            onChange={readOnly ? undefined : (e) => onUpdate({ description: e.target.value })}
          />
        </div>

        {/* Trade Price */}
        <div className="alt-field-group">
          <label>Trade Price</label>
          <div className="if-currency">
            <span className="if-currency-prefix">$</span>
            <input
              className="if-input"
              type="number"
              step="0.01"
              value={alt.tradePrice || ''}
              disabled={readOnly}
              onChange={readOnly ? undefined : (e) => onUpdate({ tradePrice: e.target.value, lastEdited: 'markup' })}
            />
          </div>
        </div>

        {/* Qty */}
        <div className="alt-field-group">
          <label>Qty</label>
          <input
            className="if-input"
            type="number"
            step="1"
            min="0"
            value={alt.qty || ''}
            disabled={readOnly}
            onChange={readOnly ? undefined : (e) => {
              const newQty = e.target.value;
              const patch = { qty: newQty };
              if (Number.isFinite(alt.unitShipping)) patch.shipping = Number(alt.unitShipping) * Number(newQty || 0);
              if (Number.isFinite(alt.unitTariff)) patch.tariff = Number(alt.unitTariff) * Number(newQty || 0);
              onUpdate(patch);
            }}
          />
        </div>

        {/* Sell Price */}
        <div className="alt-field-group">
          <label>Sell Price</label>
          <div className="if-currency">
            <span className="if-currency-prefix">$</span>
            <input
              className="if-input"
              type="number"
              step="0.01"
              value={sellInput}
              disabled={readOnly}
              onChange={readOnly ? undefined : (e) => { setSellInput(e.target.value); onUpdate({ lastEdited: 'sell' }); }}
              onBlur={readOnly ? undefined : handleSellBlur}
            />
          </div>
        </div>

        {/* Markup */}
        <div className="alt-field-group">
          <label>Markup</label>
          <div className="inline-inputs">
            <select
              className="if-select"
              value={alt.markup?.type || '%'}
              disabled={readOnly}
              onChange={readOnly ? undefined : (e) => onUpdate({ markup: { ...alt.markup, type: e.target.value }, lastEdited: 'markup' })}
            >
              <option value="%">%</option>
              <option value="fixed">Fixed</option>
            </select>
            <div className={alt.markup?.type === 'fixed' ? 'if-currency' : ''}>
              {alt.markup?.type === 'fixed' && <span className="if-currency-prefix">$</span>}
              <input
                className="if-input"
                type="number"
                step="0.01"
                value={markupInput}
                disabled={readOnly}
                onChange={readOnly ? undefined : (e) => { setMarkupInput(e.target.value); onUpdate({ lastEdited: 'markup' }); }}
                onBlur={readOnly ? undefined : handleMarkupBlur}
              />
            </div>
          </div>
        </div>

        {/* Image URL */}
        <div className="alt-field-group alt-field-wide">
          <label>Image URL</label>
          <input
            className="if-input"
            placeholder="https://..."
            value={alt.imageUrl || ''}
            readOnly={readOnly}
            onChange={readOnly ? undefined : (e) => onUpdate({ imageUrl: e.target.value })}
          />
        </div>

        {/* Vendor */}
        <div className="alt-field-group">
          <label>Vendor</label>
          <input
            className="if-input"
            placeholder="Vendor name"
            value={alt.vendor || ''}
            readOnly={readOnly}
            onChange={readOnly ? undefined : (e) => onUpdate({ vendor: e.target.value })}
          />
        </div>

        {/* Product Link */}
        <div className="alt-field-group alt-field-wide">
          <label>Product Link</label>
          <input
            className="if-input"
            type="text"
            placeholder="Product link (optional)"
            value={alt.link || ''}
            readOnly={readOnly}
            onChange={readOnly ? undefined : (e) => onUpdate({ link: e.target.value })}
          />
        </div>

        {/* Color / Material / Finish */}
        <div className="alt-field-group">
          <label>Color / Finish</label>
          <input
            className="if-input"
            type="text"
            placeholder="Color / Finish"
            value={alt.color || ''}
            readOnly={readOnly}
            onChange={readOnly ? undefined : (e) => onUpdate({ color: e.target.value })}
          />
        </div>
        <div className="alt-field-group">
          <label>Material</label>
          <input
            className="if-input"
            type="text"
            placeholder="Material"
            value={alt.materials || ''}
            readOnly={readOnly}
            onChange={readOnly ? undefined : (e) => onUpdate({ materials: e.target.value })}
          />
        </div>

        {/* Dimensions */}
        <div className="alt-field-group alt-field-wide">
          <label>Dimensions</label>
          <div className="li-dimensions-row">
            <input
              className="if-input li-spec-dim"
              type="text"
              placeholder="W&quot;"
              value={alt.dimensions?.width ?? ''}
              readOnly={readOnly}
              onChange={readOnly ? undefined : (e) => updateDimension('width', e.target.value)}
            />
            <input
              className="if-input li-spec-dim"
              type="text"
              placeholder="L&quot;"
              value={alt.dimensions?.length ?? ''}
              readOnly={readOnly}
              onChange={readOnly ? undefined : (e) => updateDimension('length', e.target.value)}
            />
            <input
              className="if-input li-spec-dim"
              type="text"
              placeholder="H&quot;"
              value={alt.dimensions?.height ?? ''}
              readOnly={readOnly}
              onChange={readOnly ? undefined : (e) => updateDimension('height', e.target.value)}
            />
            <input
              className="if-input li-spec-dim"
              type="text"
              placeholder="D&quot;"
              value={alt.dimensions?.depth ?? ''}
              readOnly={readOnly}
              onChange={readOnly ? undefined : (e) => updateDimension('depth', e.target.value)}
            />
          </div>
        </div>

        {/* Custom Specs */}
        <div className="alt-field-group alt-field-wide">
          <CustomSpecsEditor
            specs={alt.customSpecs}
            onChange={(customSpecs) => onUpdate({ customSpecs })}
            readOnly={readOnly}
            title="Custom specs"
            inputClassName="if-input"
            compact
          />
        </div>
      </div>
    </div>
  );
}
